import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api.js';
import { BrandMark, BrandWordmark } from '../components/BrandMark.js';
import { currentBrand } from '../lib/brand.js';
import { ForgotPassword, ResetPassword } from './PasswordReset.js';

type Mode = 'password' | 'token';

function friendly(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'invalid_credentials': return 'Incorrect email or password.';
      case 'invalid_token': return 'That login token is not valid or has been revoked.';
      case 'account_disabled': return 'This account is disabled. Contact an admin.';
      case 'too_many_attempts': return 'Too many attempts. Wait a few minutes and try again.';
      case 'invite_invalid': return 'This invite link is invalid or has expired. Ask an admin for a new one.';
      case 'reset_invalid': return 'This reset link is invalid, has expired, or was already used. Request a new one.';
      case 'weak_password': return err.message;
      case 'invalid_body': return 'Please fill in all fields.';
    }
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

function ssoErrorText(code: string): string | null {
  if (code === 'not_allowed') {
    return `Your Google account is not invited to ${currentBrand().name}. Ask an admin to invite you.`;
  }
  if (code === 'sso_failed') return 'Google sign-in failed. Please try again.';
  if (code === 'sso_state') return 'Sign-in session expired. Please try again.';
  return null;
}

export function LoginPage({
  onSignedIn,
  ssoConfigured,
  initialError,
  inviteToken,
  onInviteDone,
  resetToken = null,
  onResetDone,
  resetEmailEnabled = false,
}: {
  onSignedIn: () => void;
  ssoConfigured: boolean;
  initialError: string | null;
  inviteToken: string | null;
  onInviteDone: () => void;
  resetToken?: string | null;
  onResetDone?: () => void;
  resetEmailEnabled?: boolean;
}): JSX.Element {
  const [mode, setMode] = useState<Mode>('password');
  const [tokenOpen, setTokenOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(
    initialError ? ssoErrorText(initialError) ?? initialError : null,
  );

  if (inviteToken) {
    return <SetPassword inviteToken={inviteToken} onSignedIn={onSignedIn} onCancel={onInviteDone} />;
  }
  if (resetToken) {
    return <ResetPassword resetToken={resetToken} onSignedIn={onSignedIn} onCancel={onResetDone ?? onInviteDone} />;
  }
  if (forgotOpen) {
    return (
      <ForgotPassword
        initialEmail={email.trim()}
        emailEnabled={resetEmailEnabled}
        onBack={() => { setForgotOpen(false); setError(null); }}
      />
    );
  }

  async function submit(e: FormEvent, which: Mode): Promise<void> {
    e.preventDefault();
    if (busy) return;
    const mode = which;
    setMode(which);
    setBusy(true);
    setError(null);
    try {
      if (mode === 'password') await api.login(email.trim(), password);
      else await api.tokenLogin(token.trim());
      setPassword('');
      setToken('');
      onSignedIn();
    } catch (err) {
      setError(friendly(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-card__head">
          <div className="auth-card__mark auth-card__mark--wordmark">
            <BrandWordmark size="lg" />
          </div>
          <h1 className="auth-card__title visually-hidden">{currentBrand().name}</h1>
          <p className="auth-card__sub">{currentBrand().tagline}.</p>
        </div>

        {error && <div className="form-error" role="alert">{error}</div>}

        <form className="auth-form" onSubmit={(e) => void submit(e, 'password')}>
          <div className="form-row">
            <label className="form-label" htmlFor="loginEmail">Email address</label>
            <input
              id="loginEmail"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              autoFocus
              required
            />
          </div>
          <div className="form-row">
            <div className="form-label-row">
              <label className="form-label" htmlFor="loginPassword">Password</label>
              <button type="button" className="auth-link" onClick={() => { setForgotOpen(true); setError(null); }}>
                Forgot password?
              </button>
            </div>
            <input
              id="loginPassword"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn" disabled={busy || !email.trim() || !password}>
            {busy && mode === 'password' ? 'Signing in…' : 'Sign in →'}
          </button>
        </form>

        <div className="auth-divider">or</div>

        <div className="auth-alt">
          {!tokenOpen ? (
            <button
              type="button"
              className="auth-alt__toggle"
              aria-expanded={false}
              onClick={() => { setTokenOpen(true); setError(null); }}
            >
              Sign in with team token
            </button>
          ) : (
            <form className="auth-form" style={{ gap: 10 }} onSubmit={(e) => void submit(e, 'token')}>
              <div className="auth-alt__head">
                <label htmlFor="loginToken">Sign in with team token</label>
                <button
                  type="button"
                  className="btn btn--tertiary btn--sm"
                  onClick={() => { setTokenOpen(false); setToken(''); setError(null); }}
                >
                  Cancel
                </button>
              </div>
              <div className="form-row">
                <input
                  id="loginToken"
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="your-token"
                  autoFocus
                  required
                />
                <span className="form-help">
                  Type or paste your personal team token (short memorable or long). It signs you straight
                  into your own account.
                </span>
              </div>
              <button type="submit" className="btn btn--soft" disabled={busy || !token.trim()}>
                {busy && mode === 'token' ? 'Signing in…' : 'Continue with token'}
              </button>
            </form>
          )}

          {ssoConfigured && (
            <a className="btn btn--ghost" style={{ width: '100%', height: 40 }} href="/api/auth/google/start">
              Sign in with Google
            </a>
          )}
        </div>

        <div className="auth-foot">
          <span className="auth-status">Secure sign-in</span>
          <span>Need access? Ask an admin.</span>
        </div>
      </div>
    </div>
  );
}

function SetPassword({
  inviteToken,
  onSignedIn,
  onCancel,
}: {
  inviteToken: string;
  onSignedIn: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [info, setInfo] = useState<{ email: string; name: string | null } | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.checkInvite(inviteToken).then(
      (r) => { setInfo(r); setName(r.name ?? ''); },
      (err) => setCheckError(friendly(err)),
    );
  }, [inviteToken]);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (busy) return;
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.register(inviteToken, password, name.trim() || undefined);
      onSignedIn();
    } catch (err) {
      setError(friendly(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-card__head">
          <div className="auth-card__mark"><BrandMark size="lg" /></div>
          <h1 className="auth-card__title">Set your password</h1>
          <p className="auth-card__sub">
            {info ? <>Finish setting up <strong>{info.email}</strong></> : 'Checking your invite…'}
          </p>
        </div>
        {checkError && <div className="form-error">{checkError}</div>}
        {error && <div className="form-error">{error}</div>}
        {info && (
          <form className="auth-form" onSubmit={(e) => void submit(e)}>
            <input type="email" autoComplete="username" value={info.email} readOnly hidden />
            <div className="form-row">
              <label className="form-label" htmlFor="regName">Your name</label>
              <input id="regName" value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
            </div>
            <div className="form-row">
              <label className="form-label" htmlFor="regPassword">New password</label>
              <input
                id="regPassword"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={10}
                required
                autoFocus
              />
              <span className="form-help">At least 10 characters.</span>
            </div>
            <div className="form-row">
              <label className="form-label" htmlFor="regConfirm">Confirm password</label>
              <input
                id="regConfirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn" disabled={busy || !password || !confirm}>
              {busy ? 'Saving…' : 'Set password and sign in'}
            </button>
          </form>
        )}
        <div className="auth-foot auth-foot--center">
          <button type="button" className="btn btn--tertiary btn--sm" onClick={onCancel}>
            Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
}
